// src/_store/masterAuthSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const baseUrl = `${process.env.REACT_APP_API_URL}`;

// ============================================
// ASYNC THUNKS - MASTER AUTH
// ============================================

export const masterLogin = createAsyncThunk(
  'masterAuth/masterLogin',
  async (password, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/auth/master-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      
      if (data.success && data.data?.token) {
        sessionStorage.setItem('masterToken', data.data.token);
        sessionStorage.setItem('masterTokenExpiry', data.data.expiry);
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// In masterAuthSlice.js - update fetchAllUsers
export const fetchAllUsers = createAsyncThunk(
  'masterAuth/fetchAllUsers',
  async ({ page = 1, per_page = 20, search = '', provider_type = '', verified = '', status = '', sort_by = 'partner_no', sort_order = 'asc' } = {}, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      
      if (!token) {
        return rejectWithValue({ message: 'No session found' });
      }
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', per_page);
      if (search) params.append('search', search);
      if (provider_type) params.append('provider_type', provider_type);
      if (verified) params.append('verified', verified);
      if (status) params.append('status', status);
      if (sort_by) params.append('sort_by', sort_by);
      if (sort_order) params.append('sort_order', sort_order);
      
      const response = await fetch(`${baseUrl}/super-admin/get-users-list?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const masterLogout = createAsyncThunk(
  'masterAuth/masterLogout',
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      if (token) {
        await fetch(`${baseUrl}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
      }
      
      sessionStorage.removeItem('masterToken');
      sessionStorage.removeItem('masterTokenExpiry');
      
      return { success: true };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// ============================================
// ASYNC THUNKS - SUPER ADMIN
// ============================================

export const fetchAllJobs = createAsyncThunk(
  'masterAuth/fetchAllJobs',
  async ({ status, limit, offset, sort, order } = {}, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      
      if (!token) {
        return rejectWithValue({ message: 'No session found' });
      }
      
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (limit) params.append('limit', limit);
      if (offset) params.append('offset', offset);
      if (sort) params.append('sort', sort);
      if (order) params.append('order', order);
      
      const response = await fetch(`${baseUrl}/super-admin/all-jobs?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchJobDetails = createAsyncThunk(
  'masterAuth/fetchJobDetails',
  async (jobId, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      
      if (!token) {
        return rejectWithValue({ message: 'No session found' });
      }
      
      const response = await fetch(`${baseUrl}/super-admin/job/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateJobStatus = createAsyncThunk(
  'masterAuth/updateJobStatus',
  async ({ jobId, updates }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      
      if (!token) {
        return rejectWithValue({ message: 'No session found' });
      }
      
      const response = await fetch(`${baseUrl}/super-admin/update-job-status/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates),
      });
      
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { jobId, updates, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const deleteJob = createAsyncThunk(
  'masterAuth/deleteJob',
  async (jobId, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      
      if (!token) {
        return rejectWithValue({ message: 'No session found' });
      }
      
      const response = await fetch(`${baseUrl}/super-admin/delete-job/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { jobId, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const bulkDeleteJobs = createAsyncThunk(
  'masterAuth/bulkDeleteJobs',
  async (jobIds, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('masterToken');
      
      if (!token) {
        return rejectWithValue({ message: 'No session found' });
      }
      
      const response = await fetch(`${baseUrl}/super-admin/bulk-delete-jobs`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ jobIds }),
      });
      
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { jobIds, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  isAuthenticated: !!sessionStorage.getItem('masterToken'),
  token: sessionStorage.getItem('masterToken') || null,
  tokenExpiry: sessionStorage.getItem('masterTokenExpiry') || null,
  loginStatus: 'idle',
  success: false,
  jobs: [],
  selectedJob: null,
  totalJobs: 0,
  loading: false,
  error: null,
  fetchStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  // Add users state
  users: [],
  totalUsers: 0,
  usersLoading: false,
  usersError: null,
  usersFetchStatus: 'idle',
};

// ============================================
// SLICE DEFINITION
// ============================================

const masterAuthSlice = createSlice({
  name: 'masterAuth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearSelectedJob: (state) => {
      state.selectedJob = null;
    },
    resetAuthState: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.tokenExpiry = null;
      state.loginStatus = 'idle';
      state.success = false;
      sessionStorage.removeItem('masterToken');
      sessionStorage.removeItem('masterTokenExpiry');
    },
    resetAdminState: (state) => {
      state.jobs = [];
      state.selectedJob = null;
      state.totalJobs = 0;
      state.loading = false;
      state.error = null;
      state.fetchStatus = 'idle';
      state.updateStatus = 'idle';
      state.deleteStatus = 'idle';
    },
    resetUsersState: (state) => {
      state.users = [];
      state.totalUsers = 0;
      state.usersLoading = false;
      state.usersError = null;
      state.usersFetchStatus = 'idle';
    },
    clearUsersError: (state) => {
      state.usersError = null;
    },
    checkSessionStorageAuth: (state) => {
      const token = sessionStorage.getItem('masterToken');
      const expiry = sessionStorage.getItem('masterTokenExpiry');
      
      if (token && expiry) {
        const expiryDate = new Date(expiry);
        if (expiryDate > new Date()) {
          state.isAuthenticated = true;
          state.token = token;
          state.tokenExpiry = expiry;
        } else {
          sessionStorage.removeItem('masterToken');
          sessionStorage.removeItem('masterTokenExpiry');
          state.isAuthenticated = false;
          state.token = null;
          state.tokenExpiry = null;
        }
      } else {
        state.isAuthenticated = false;
        state.token = null;
        state.tokenExpiry = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(masterLogin.pending, (state) => {
        state.loginStatus = 'loading';
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(masterLogin.fulfilled, (state, action) => {
        state.loginStatus = 'succeeded';
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload?.data?.token || null;
        state.tokenExpiry = action.payload?.data?.expiry || null;
        state.success = true;
      })
      .addCase(masterLogin.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload?.message || 'Login failed';
        state.success = false;
      })
      .addCase(masterLogout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.tokenExpiry = null;
        state.success = false;
        state.jobs = [];
        state.selectedJob = null;
        state.totalJobs = 0;
      })
      .addCase(fetchAllJobs.pending, (state) => {
        state.fetchStatus = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllJobs.fulfilled, (state, action) => {
        state.fetchStatus = 'succeeded';
        state.loading = false;
        state.jobs = action.payload?.data || [];
        state.totalJobs = action.payload?.total || 0;
      })
      .addCase(fetchAllJobs.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch jobs';
      })
      .addCase(fetchJobDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJob = action.payload?.data || null;
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch job details';
      })
      .addCase(updateJobStatus.pending, (state) => {
        state.updateStatus = 'loading';
        state.error = null;
      })
      .addCase(updateJobStatus.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const index = state.jobs.findIndex(j => j._id === action.payload.jobId);
        if (index !== -1) {
          state.jobs[index] = { ...state.jobs[index], ...action.payload.updates };
        }
        if (state.selectedJob?._id === action.payload.jobId) {
          state.selectedJob = { ...state.selectedJob, ...action.payload.updates };
        }
      })
      .addCase(updateJobStatus.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload?.message || 'Failed to update job';
      })
      .addCase(deleteJob.pending, (state) => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.jobs = state.jobs.filter(j => j._id !== action.payload.jobId);
        state.totalJobs -= 1;
        if (state.selectedJob?._id === action.payload.jobId) {
          state.selectedJob = null;
        }
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload?.message || 'Failed to delete job';
      })
      .addCase(bulkDeleteJobs.pending, (state) => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(bulkDeleteJobs.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.jobs = state.jobs.filter(j => !action.payload.jobIds.includes(j._id));
        state.totalJobs -= action.payload.jobIds.length;
        if (state.selectedJob && action.payload.jobIds.includes(state.selectedJob._id)) {
          state.selectedJob = null;
        }
      })
      .addCase(bulkDeleteJobs.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload?.message || 'Failed to delete jobs';
      })
      // Add users cases
      .addCase(fetchAllUsers.pending, (state) => {
        state.usersFetchStatus = 'loading';
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.usersFetchStatus = 'succeeded';
        state.usersLoading = false;
        state.users = action.payload?.data || [];
        state.totalUsers = action.payload?.count || 0;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.usersFetchStatus = 'failed';
        state.usersLoading = false;
        state.usersError = action.payload?.message || 'Failed to fetch users';
      });
  },
});

// Export all actions
export const { 
  clearError, 
  clearSuccess, 
  clearSelectedJob,
  resetAuthState,
  resetAdminState,
  resetUsersState,
  clearUsersError,
  checkSessionStorageAuth,
} = masterAuthSlice.actions;

// Job selectors
export const selectIsAuthenticated = (state) => state.masterAuth?.isAuthenticated || false;
export const selectToken = (state) => state.masterAuth?.token || null;
export const selectLoginStatus = (state) => state.masterAuth?.loginStatus || 'idle';
export const selectSuccess = (state) => state.masterAuth?.success || false;
export const selectAllJobs = (state) => state.masterAuth?.jobs || [];
export const selectSelectedJob = (state) => state.masterAuth?.selectedJob || null;
export const selectTotalJobs = (state) => state.masterAuth?.totalJobs || 0;
export const selectLoading = (state) => state.masterAuth?.loading || false;
export const selectError = (state) => state.masterAuth?.error || null;
export const selectFetchStatus = (state) => state.masterAuth?.fetchStatus || 'idle';
export const selectUpdateStatus = (state) => state.masterAuth?.updateStatus || 'idle';
export const selectDeleteStatus = (state) => state.masterAuth?.deleteStatus || 'idle';

// User selectors
export const selectAllUsers = (state) => state.masterAuth?.users || [];
export const selectTotalUsers = (state) => state.masterAuth?.totalUsers || 0;
export const selectUsersLoading = (state) => state.masterAuth?.usersLoading || false;
export const selectUsersError = (state) => state.masterAuth?.usersError || null;
export const selectUsersFetchStatus = (state) => state.masterAuth?.usersFetchStatus || 'idle';

export default masterAuthSlice.reducer;